// src/components/Seo/SeoManager.jsx
//
// Applies per-route <title>, description, canonical and Open Graph tags.
//
// Written against document.head directly rather than pulling in react-helmet:
// one dependency-free effect does the whole job, and adding a package to a
// build that is already the weak point of this deployment is not worth it.
//
// This does NOT make the app server-rendered. Google applies these when it
// renders the page, which is a second pass that can lag the crawl by days — so
// this fixes canonicalisation and per-page targeting, not the underlying fact
// that the HTML ships empty. Prerendering is the fix for that.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE_ORIGIN, seoForPath } from "../../utils/seoConfig";

/** Production host. Anything else — previews, localhost — must not be indexed. */
const CANONICAL_HOST = "www.journim.com";

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== "content") el.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  el.setAttribute("content", attrs.content);
  return el;
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const SeoManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const { title, description, noindex } = seoForPath(pathname);

    // A preview deployment is a byte-for-byte copy of production. Indexed, it
    // competes with the real site, so every non-production host is noindexed
    // here as well as by the X-Robots-Tag header in vercel.json.
    const offProduction =
      typeof window !== "undefined" && window.location.hostname !== CANONICAL_HOST;
    const blocked = noindex || offProduction;

    const canonical = blocked ? null : `${SITE_ORIGIN}${pathname === "/" ? "/" : pathname}`;

    document.title = title;
    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: blocked ? "noindex, nofollow" : "index, follow",
    });
    upsertCanonical(canonical);

    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    if (canonical) {
      upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    }
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description,
    });
  }, [pathname]);

  return null;
};

export default SeoManager;
